import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../ui/card';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { Separator } from '../../ui/separator';
import IconWrapper from '../../IconWrapper';
import { FiExternalLink, FiPlay, FiChevronDown, FiChevronUp, FiTrash2, FiMail, FiPhone } from 'react-icons/fi';
import { ProcessingStatus } from '../../../types/signals-live';
import { useSignalProgress } from '../../../hooks/useRealtimeUpdates';
import { Timestamp } from '../../ui/timestamp';
import SignalProcessingStatusBadge from './SignalProcessingStatusBadge';

interface SignalSummaryCardProps {
  signal: {
    id: string;
    source_id: string;
    customer_name: string;
    customer_email?: string;
    customer_phone?: string;
    summary: string;
    verbatim?: string;
    summary_detailed?: string;
    channel: string;
    journey_stage?: string;
    severity_score?: number;
    created_at: string;
    processing_status?: ProcessingStatus;
    status?: string;
    complaint_detected?: boolean;
    complaint_confidence?: string;
    classification?: {
      is_complaint?: boolean;
      complaint_confidence?: string;
    };
  };
  onViewSignal: (signalId: string) => void;
  onStartSimulation: (signalId: string) => void;
  onFinalizeDraft?: (signalId: string) => void;
  onDiscardDraft?: (signalId: string) => void;
  isLoading?: boolean;
}

const SignalSummaryCard: React.FC<SignalSummaryCardProps> = ({
  signal,
  onViewSignal,
  onStartSimulation,
  onFinalizeDraft,
  onDiscardDraft,
  isLoading = false
}) => {
  const [isVerbatimExpanded, setIsVerbatimExpanded] = useState(false);
  const navigate = useNavigate();
  
  // Subscribe to real-time progress updates
  const { progressInfo } = useSignalProgress(signal.id);

  // Generate initials from customer name
  const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getChannelIcon = (channel: string) => {
    const channelLower = channel.toLowerCase();
    if (channelLower.includes('email')) {
      return <IconWrapper icon={FiMail} className="h-3.5 w-3.5" />;
    }
    if (channelLower.includes('chat')) return '💬';
    if (channelLower.includes('phone')) {
      return <IconWrapper icon={FiPhone} className="h-3.5 w-3.5" />;
    }
    if (channelLower.includes('review')) return '⭐';
    return '📄';
  };

  const handleViewSignal = () => {
    onViewSignal(signal.id);
    navigate(`/v3/signals/${signal.id}`);
  };

  const handleStartSimulation = async () => {
    await onStartSimulation(signal.id);
  };
  
  // Use summary_detailed or truncated verbatim for summary, verbatim for details
  const summaryText = signal.summary_detailed || signal.summary || (signal.verbatim ? signal.verbatim.substring(0, 150) + '...' : 'No summary');
  const verbatimToShow = signal.verbatim;
  const shouldTruncate = verbatimToShow && verbatimToShow.length > 200;
  const displayVerbatim = shouldTruncate && !isVerbatimExpanded
    ? verbatimToShow.substring(0, 200) + '...'
    : verbatimToShow;
  
  // Only show details if verbatim exists and is different from summary
  const showDetails = verbatimToShow && verbatimToShow !== summaryText;

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarFallback className="bg-muted text-foreground font-semibold">
              {getInitials(signal.customer_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <CardTitle className="text-lg truncate">{signal.customer_name}</CardTitle>
              <SignalProcessingStatusBadge
                processingStatus={signal.processing_status}
                status={signal.status}
                progressStage={progressInfo?.stage}
                progressPercent={progressInfo?.percent}
                className="shrink-0"
              />
            </div>
            <CardDescription className="text-xs">
              {signal.source_id} • <Timestamp value={signal.created_at} format="absolute" />
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Customer Info */}
        {(signal.customer_email || signal.customer_phone) && (
          <div className="text-sm text-muted-foreground space-y-1">
            {signal.customer_email && (
              <div className="flex items-center gap-2">
                <IconWrapper icon={FiMail} className="h-4 w-4 shrink-0" />
                <span>{signal.customer_email}</span>
              </div>
            )}
            {signal.customer_phone && (
              <div className="flex items-center gap-2">
                <IconWrapper icon={FiPhone} className="h-4 w-4 shrink-0" />
                <span>{signal.customer_phone}</span>
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-2">Summary</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">{summaryText}</p>
        </div>

        {/* Details (only show if verbatim exists and is different from summary) */}
        {showDetails && (
          <div>
            <Collapsible open={isVerbatimExpanded} onOpenChange={setIsVerbatimExpanded}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-foreground">Details</h4>
                {shouldTruncate && (
                  <CollapsibleTrigger asChild>
                    <button className="text-xs text-primary hover:underline flex items-center gap-1 transition-colors">
                      {isVerbatimExpanded ? (
                        <>
                          <IconWrapper icon={FiChevronUp} className="h-3 w-3" />
                          Show less
                        </>
                      ) : (
                        <>
                          <IconWrapper icon={FiChevronDown} className="h-3 w-3" />
                          Show more
                        </>
                      )}
                    </button>
                  </CollapsibleTrigger>
                )}
              </div>
              {!shouldTruncate ? (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {verbatimToShow}
                </p>
              ) : (
                <>
                  {!isVerbatimExpanded && (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {displayVerbatim}
                    </p>
                  )}
                  <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {verbatimToShow}
                    </p>
                  </CollapsibleContent>
                </>
              )}
            </Collapsible>
          </div>
        )}

        {/* Metadata */}
        <div className="space-y-2">
          <Separator />
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              {getChannelIcon(signal.channel)}
              <span className="capitalize">{signal.channel.replace('_', ' ')}</span>
            </div>
            {signal.journey_stage && (
              <>
                <span>•</span>
                <span className="capitalize">{signal.journey_stage.replace('_', ' ')}</span>
              </>
            )}
          </div>
        </div>
      </CardContent>

      <CardFooter className="flex gap-2 pt-4">
        <Button
          variant="outline"
          onClick={handleViewSignal}
          className="flex-1"
          disabled={isLoading}
        >
          <IconWrapper icon={FiExternalLink} className="h-4 w-4 mr-2" />
          View Full Signal
        </Button>
        {signal.status === 'draft' && onFinalizeDraft ? (
          <>
            {onDiscardDraft && (
              <Button
                variant="outline"
                onClick={() => onDiscardDraft(signal.id)}
                disabled={isLoading}
                className="flex-1"
              >
                <IconWrapper icon={FiTrash2} className="h-4 w-4 mr-2" />
                Discard
              </Button>
            )}
            <Button
              onClick={() => onFinalizeDraft(signal.id)}
              className="flex-1"
              disabled={isLoading}
              loading={isLoading}
            >
              <IconWrapper icon={FiPlay} className="h-4 w-4 mr-2" />
              Save Feedback
            </Button>
          </>
        ) : (
          <Button
            onClick={handleStartSimulation}
            className="flex-1"
            disabled={isLoading}
            loading={isLoading}
          >
            <IconWrapper icon={FiPlay} className="h-4 w-4 mr-2" />
            Start Case Guidance
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default SignalSummaryCard;

